import React, { useEffect, useMemo, useState } from "react";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { productData } from "settings/constants/constants";
import {
  createKontrolTicket,
  createTicket,
  type CreateTicketPayload,
} from "../request/request";
import {
  PRIORITY_OPTIONS,
  extractUniqueCustomers,
  filterServicesByCustomer,
  findProductByLabel,
  findTopicByLabel,
  findTwoLevelByLabel,
  getEndCustomerId,
  getServiceSelectionError,
  isServiceSelectionValid,
  mapCRPriority,
  type PriorityLabel,
} from "../utils/tickets";
import { useLoadServiceItems } from "../hooks/useLoadServiceItems";
import { useTopicsLoader } from "../hooks/useTopicsLoader";
import { useChangeRequestForm } from "../hooks/useChangeRequestForm";
import { KontrolEdgeServiceSelection } from "./shared/KontrolEdgeServiceSelection";
import { RequestModalShell, type RequestStep } from "./requestForm/RequestModalShell";
import { FieldRow, FormField } from "./requestForm/FormField";
import { SummaryFields } from "./requestForm/SummaryFields";
import { AttachmentDropzone } from "./requestForm/AttachmentDropzone";
import { ContactResponseFields } from "./requestForm/ContactResponseFields";
import {
  createEmptyContact,
  isContactValid,
  toContactPayload,
} from "./requestForm/contactDetails";
import { isDescriptionValid, isTitleValid } from "./requestForm/fieldRules";
import { useRequestSteps } from "./requestForm/useRequestSteps";
import { useTicketSubmission } from "./requestForm/useTicketSubmission";
import {
  NewTicketParticipants,
  addParticipantsOnceCreated,
  useNewTicketParticipants,
} from "./requestForm/NewTicketParticipants";

interface ChangeRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTicketCreated?: () => void;
  /**
   * A request someone else drafted — Archi's "Order now" on a proposed
   * scenario. Fills the title and description when the modal opens; the user
   * still picks the services, reviews and sends it.
   */
  prefill?: { title: string; description: string } | null;
}

// The CTX Reflector desk still expects these two, though they never vary.
const CLOUD = "CloudGlobal";
const REGION = "us-east-1";

const KE_STEPS: RequestStep[] = [
  { id: "services", title: "Services" },
  { id: "details", title: "Details" },
  { id: "attachments", title: "Files & people" },
];
const REFLECTOR_STEPS: RequestStep[] = [
  { id: "topic", title: "Topic" },
  { id: "details", title: "Details" },
  { id: "attachments", title: "Attachments & contact" },
];

export const ChangeRequestModal: React.FC<ChangeRequestModalProps> = ({
  isOpen,
  onClose,
  onTicketCreated,
  prefill = null,
}) => {
  const api = useAxiosAuth0();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isPartnerView = tenantId === partnerId;
  const globalProduct = useAppSelector(
    (state) => state.product.selectedProduct
  );
  const isKontrolEdge = globalProduct === "Kontrol Edge";

  const topicsLoader = useTopicsLoader({
    api,
    globalProduct: globalProduct || "",
    isKontrolEdge,
  });
  const form = useChangeRequestForm();
  const { formState } = form;
  const [contact, setContact] = useState(createEmptyContact);
  const submission = useTicketSubmission(onTicketCreated);

  const { setTitle, setDescription } = form;
  useEffect(() => {
    if (!isOpen || !prefill) return;
    setTitle(prefill.title);
    setDescription(prefill.description);
  }, [isOpen, prefill, setTitle, setDescription]);

  const { items: serviceItems } = useLoadServiceItems(
    api,
    tenantId,
    isKontrolEdge
  );
  const uniqueCustomers = useMemo(
    () => extractUniqueCustomers(serviceItems),
    [serviceItems]
  );
  // Outside partner view the user only ever sees their own services.
  const filteredServiceItems = useMemo(
    () =>
      isPartnerView
        ? filterServicesByCustomer(serviceItems, formState.selectedCustomerId)
        : serviceItems,
    [serviceItems, formState.selectedCustomerId, isPartnerView]
  );

  const hasServices = isServiceSelectionValid(
    isPartnerView,
    formState.selectedCustomerId,
    formState.selectedServiceIds
  );
  const endCustomerId = getEndCustomerId(
    isPartnerView,
    formState.selectedCustomerId,
    tenantId
  );
  const { participants, setParticipants, resetParticipants } =
    useNewTicketParticipants(isKontrolEdge ? endCustomerId : undefined);
  const steps = isKontrolEdge ? KE_STEPS : REFLECTOR_STEPS;
  const stepper = useRequestSteps(steps, {
    services: hasServices,
    topic:
      topicsLoader.selectedTopicLabel.trim() !== "" &&
      topicsLoader.selectedTwoLevelLabel.trim() !== "",
    details:
      formState.priority !== "" &&
      isTitleValid(formState.title) &&
      isDescriptionValid(formState.description),
    attachments: isKontrolEdge || isContactValid(contact),
  });

  const handleClose = () => {
    topicsLoader.resetTopics();
    form.resetForm();
    setContact(createEmptyContact());
    stepper.resetSteps();
    submission.resetSubmission();
  resetParticipants();
    onClose();
  };

  const priorityNumber = () =>
    mapCRPriority(formState.priority as PriorityLabel, isKontrolEdge);

  const createKontrolEdgeTicket = () => {
    const serviceError = getServiceSelectionError(
      isPartnerView,
      formState.selectedCustomerId,
      formState.selectedServiceIds
    );
    if (serviceError) {
      submission.refuse(serviceError);
      return;
    }
    void submission.submit(() =>
      createKontrolTicket(api, {
        requesttype: "CHANGE_REQUEST",
        summary: formState.title,
        priority: priorityNumber(),
        description: formState.description,
        subscriptionID: formState.selectedSubscriptionIds,
        serviceType: formState.selectedServiceTypes,
        files: formState.attachmentFiles,
        end_customer: endCustomerId,
      }),
      addParticipantsOnceCreated(api, participants)
    );
  };

  const createReflectorTicket = () => {
    const payload: CreateTicketPayload = {
      Tipo: "Change Request",
      product: findProductByLabel(productData, globalProduct || "")?.value ?? "",
      cloud: CLOUD,
      region: REGION,
      TipoSolicitud:
        findTopicByLabel(topicsLoader.topics, topicsLoader.selectedTopicLabel)
          ?.value ?? "",
      level_3:
        findTwoLevelByLabel(
          topicsLoader.twoLevelOptions,
          topicsLoader.selectedTwoLevelLabel
        )?.value ?? "",
      Resumen: formState.title,
      Prioridad: priorityNumber(),
      Descripcion: formState.description,
      files: formState.attachmentFiles,
      ...(contact.sendResponse ? toContactPayload(contact) : {}),
    };
    void submission.submit(() => createTicket(api, payload));
  };

  return (
    <RequestModalShell
      type="change"
      isOpen={isOpen}
      onClose={handleClose}
      steps={steps}
      activeStepId={stepper.activeStepId}
      canActivate={stepper.canActivate}
      onStepChange={stepper.goToStep}
      canContinue={stepper.canContinue}
      isSaving={submission.isSaving}
      onSubmit={isKontrolEdge ? createKontrolEdgeTicket : createReflectorTicket}
      submitError={submission.submitError}
      created={submission.created}
    >
      {stepper.activeStepId === "services" && (
        <KontrolEdgeServiceSelection
          isPartnerView={isPartnerView}
          uniqueCustomers={uniqueCustomers}
          filteredServiceItems={filteredServiceItems}
          selectedCustomerId={formState.selectedCustomerId}
          selectedCustomerNameDisplay={formState.selectedCustomerNameDisplay}
          selectedServiceLabels={formState.selectedServiceLabels}
          onCustomerSelect={(name) =>
            form.handleCustomerSelect(name, uniqueCustomers)
          }
          onServiceSelect={(labels) =>
            form.handleServiceSelect(labels, filteredServiceItems)
          }
          servicesHint="The service(s) you want to change."
        />
      )}

      {stepper.activeStepId === "topic" && (
        <FieldRow>
          <FormField label="Topic" required>
            <GenericDropdown
              items={topicsLoader.topics.map((t) => t.label)}
              value={topicsLoader.selectedTopicLabel}
              onSelect={topicsLoader.handleSelectTopic}
              loading={topicsLoader.loadingTopics}
              defaultLabel="Select a topic"
              minWidthClass=""
              maxWidthClass=""
              widthClass="w-full"
              fixedPosition={true}
              dropdownMaxHeightClass="max-h-48"
            />
          </FormField>
          <FormField
            label="Specify your request"
            required
            hint={
              topicsLoader.selectedTopicLabel ? undefined : "Pick a topic first."
            }
          >
            <GenericDropdown
              items={topicsLoader.twoLevelOptions.map((t) => t.label)}
              value={topicsLoader.selectedTwoLevelLabel}
              onSelect={topicsLoader.setSelectedTwoLevelLabel}
              loading={topicsLoader.loadingTwoLevels}
              disabled={!topicsLoader.selectedTopicLabel}
              defaultLabel="Select an option"
              minWidthClass=""
              maxWidthClass=""
              widthClass="w-full"
              fixedPosition={true}
              dropdownMaxHeightClass="max-h-48"
            />
          </FormField>
        </FieldRow>
      )}

      {stepper.activeStepId === "details" && (
        <>
          <FieldRow>
            <FormField label="Priority" required>
              <GenericDropdown
                items={[...PRIORITY_OPTIONS]}
                value={formState.priority}
                onSelect={form.setPriority}
                defaultLabel="Select priority"
                minWidthClass=""
                maxWidthClass=""
                widthClass="w-full"
                fixedPosition={true}
              />
            </FormField>
          </FieldRow>
          <SummaryFields
            title={formState.title}
            description={formState.description}
            onTitleChange={form.setTitle}
            onDescriptionChange={form.setDescription}
            titlePlaceholder="What do you want to change?"
            descriptionPlaceholder="Describe the change and when you need it"
          />
        </>
      )}

      {stepper.activeStepId === "attachments" && (
        <>
          <AttachmentDropzone
            files={formState.attachmentFiles}
            onChange={form.setAttachmentFiles}
          />
          {!isKontrolEdge && (
            <ContactResponseFields contact={contact} onChange={setContact} />
          )}
          {isKontrolEdge && (
            <NewTicketParticipants
              endCustomerId={endCustomerId}
              selected={participants}
              onChange={setParticipants}
            />
          )}
        </>
      )}
    </RequestModalShell>
  );
};

export default ChangeRequestModal;
